/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Divider, Grid, TextField } from '@mui/material';
import ProfileCell from '../../organisations/components/ProfileCell';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import WalletCard from '../../../components/card/WalletCard';
import { AssignCreditSteps, CreditCheckoutProps } from '../types';
import { rechargeCustomerWallet } from '../request';
import useNotifications from '../../../hooks/useNotifications';
import CustomButton from '../../../components/button/CustomButton';
import { formatCurrency } from '../../../utils/currencyFormatter';
import { useAtomValue } from 'jotai';
import { walletConfigAtom } from '../state';
import getGST from '../../../utils/currency';

const CreditCheckout = ({
  setCurrentStep,
  selectedOrg,
  handleClose,
}: CreditCheckoutProps) => {
  const [creditAmount, setCreditAmount] = useState<string>('');
  const [isAPILoading, setisAPILoading] = useState<boolean>(false);
  const walletConfig = useAtomValue(walletConfigAtom);
  const GST_Value = getGST(creditAmount);

  const { addNotification } = useNotifications();

  const totalNumberOfCredits = useMemo(() => {
    if (isNaN(parseFloat(creditAmount))) {
      return 0;
    }
    return (creditAmount ? parseFloat(creditAmount) : 0) + GST_Value;
  }, [creditAmount, GST_Value]);

  const handleSubmit = async () => {
    try {
      if (!totalNumberOfCredits) {
        addNotification({
          message: 'Please enter a valid amount',
          type: 'error',
        });
        return;
      }
      setisAPILoading(true);
      const response = await rechargeCustomerWallet({
        credits: totalNumberOfCredits,
        orgId: selectedOrg?.id,
      });
      if (response?.success) {
        addNotification({
          message: 'Credits assigned successfully',
          type: 'success',
        });
        handleClose();
      } else {
        addNotification({
          message: 'Credits assigned failed',
          type: 'error',
        });
      }
      setisAPILoading(false);
    } catch (e) {
      setisAPILoading(false);

      addNotification({
        message: (e as Error).message || 'Something went wrong',
        type: 'error',
      });
      console.log(e);
    }
  };
  const getTotalWalletAmount = () => {
    if (walletConfig.loading) {
      return 0;
    }
    if (!walletConfig.data) {
      return 0;
    }
    return walletConfig.data.balance;
  };

  if (!selectedOrg?.id) {
    return <>No Org Selected.</>;
  }

  const hasInSufficientBalance = totalNumberOfCredits > getTotalWalletAmount();
  const renderGST = () => {
    if (walletConfig?.data?.currencyCode === 'INR') {
      return (
        <Grid container gap={'12px'} alignItems="center">
          <Grid item xs={2.5}>
            <Typography
              sx={{
                color: '#2d2d2d',
                fontSize: 14,
              }}
            >
              GST (18%)
            </Typography>
          </Grid>
          <Grid item xs />
          <Grid item xs={'auto'}>
            <Typography
              sx={{
                color: '#2d2d2d',
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {formatCurrency(GST_Value, selectedOrg.currencyCode ?? 'INR')}
            </Typography>
          </Grid>
        </Grid>
      );
    }
    return null;
  };
  return (
    <Box
      sx={{
        maxHeight: 'calc(90vh - 57px)',
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <WalletCard />
        <Grid container>
          <Grid item xs={6}>
            <ProfileCell
              name={selectedOrg.name}
              secondaryText={formatCurrency(
                selectedOrg.credits ?? 0,
                selectedOrg.currencyCode ??
                  walletConfig?.data?.currencyCode ??
                  'INR'
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              variant="outlined"
              label="Enter amount"
              fullWidth
              type="text"
              error={hasInSufficientBalance}
              helperText={
                hasInSufficientBalance
                  ? 'Amount exceeds available wallet balance'
                  : ''
              }
              value={creditAmount}
              onChange={(e) => {
                const value = e.target.value;
                // Regex to allow only digits and decimal points
                const regex = /^[0-9]*\.?[0-9]*$/;
                if (regex.test(value) || value === '') {
                  setCreditAmount(value);
                }
              }}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#E0E0E9',
            borderRadius: 1,
            padding: 1.5,
          }}
        >
          <Typography
            sx={{
              color: '#585858',
              fontSize: 12,
              fontWeight: 400,
            }}
          >
            Your credits will be instantly debited
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {renderGST()}
        <Grid container gap={'12px'} alignItems="center">
          <Grid item xs={2.5}>
            <Typography
              sx={{
                color: '#2d2d2d',
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              Total Amount
            </Typography>
          </Grid>
          <Grid item xs />
          <Grid item xs={'auto'}>
            <Typography
              sx={{
                color: '#2d2d2d',
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              {formatCurrency(
                totalNumberOfCredits,
                selectedOrg.currencyCode ?? 'INR'
              )}
            </Typography>
          </Grid>
        </Grid>
        <Grid container gap={'12px'} alignItems="center">
          <Grid item xs={2.5}>
            <Button
              onClick={() =>
                setCurrentStep(AssignCreditSteps.SELECT_ORGANISATION)
              }
              variant="outlined"
              sx={{
                width: '100%',
                borderRadius: 2,
              }}
            >
              Go Back
            </Button>
          </Grid>
          <Grid item xs />
          <Grid item xs={'auto'}>
            <CustomButton
              disabled={isAPILoading || hasInSufficientBalance}
              loading={isAPILoading}
              onClick={handleSubmit}
              variant="contained"
              sx={{
                width: '100%',
                borderRadius: 2,
              }}
            >
              Assign Credits
            </CustomButton>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CreditCheckout;
